import { useCallback, useEffect, useRef, useState } from 'react';
import type { Currency } from '@/types';
import type { GuestPushContext } from '@/lib/push';
import styles from './OrderSuccessScreen.module.css';
import { PushOptInBanner, type PushOptInBannerLabels } from './PushOptInBanner';

/**
 * #603 [1] + #626: 주문 완료 페이지 — 테이블·픽업 통합.
 *
 * - 픽업: 탭 기반(고객이 버튼을 눌러야 이동) — primary "주문 내역 보기" / secondary "홈으로".
 * - 테이블(#626): 완료 인지 후 **카운트다운 자동 이동**(`autoNavigateSeconds`, 기본 미사용) —
 *   대부분의 고객이 추가 주문을 위해 메뉴로 돌아가므로, N초 뒤 primary(메뉴 더 보기)로
 *   자동 이동한다. 버튼에 남은 초를 표시해 "주문 접수됨" 인지(#603 의도)를 보존한다.
 *   "주문 현황 보기" secondary CTA 는 사용 빈도가 낮아 제거(#626) — 테이블은 단일 CTA.
 * - "이번 주문 요약"(방금 주문 · N개 · 금액) + 테이블 번호(테이블만) 표시.
 *
 * 컴포넌트는 i18n-agnostic — 호출처(CheckoutPage)가 locale 문자열을 `labels` 로 주입한다.
 * (TipSelector / AddCardModal 와 동일한 props 기반 i18n 패턴.)
 */

export type OrderSuccessOrderType = 'pickup' | 'table';

/** 방금 주문한(=cart clear 전 캡처한) delta 요약. */
export interface OrderSuccessSummary {
  itemCount: number;
  totalAmount: number;
  currency: Currency;
}

export interface OrderSuccessScreenLabels {
  title: string;
  thanks: string;
  /** "방금 주문" 접두사 (요약 행). */
  justOrderedPrefix: string;
  /** 개수 단위 ("개" / "items" 등). */
  itemCountUnit: string;
  /** 테이블 번호 라벨 빌더 (테이블 모드 전용). */
  tableLabel: (tableNumber: string) => string;
  /** 테이블 primary CTA — 계속 주문. */
  viewMenu: string;
  /** 픽업 primary CTA — 주문 내역. */
  viewOrders: string;
  /** 픽업 secondary CTA — 홈으로. */
  goHome: string;
}

interface OrderSuccessScreenProps {
  orderType: OrderSuccessOrderType;
  /** 테이블 번호 (테이블 모드에서만 표시). */
  tableNumber?: string;
  /** 방금 주문 요약 (cart clear 전 캡처). 없으면 요약 행 미표시. */
  justOrderedSummary?: OrderSuccessSummary;
  labels: OrderSuccessScreenLabels;
  /** 금액 포매터 (요약 표시용). justOrderedSummary 가 있으면 필수. */
  formatCurrency?: (amount: number, currency: Currency) => string;
  /**
   * #626: N초 카운트다운 후 primary 자동 실행 (테이블 — 메뉴 자동 복귀).
   * 미지정/0 이면 자동 이동 없음(픽업 — #603 탭 기반 유지).
   * 사용자가 먼저 버튼을 누르면 자동 이동은 무효(hasNavigatedRef 가드).
   */
  autoNavigateSeconds?: number;
  /** primary CTA — 테이블: 메뉴 더 보기 / 픽업: 주문 내역 보기. */
  onPrimary: () => void;
  /** secondary CTA — 픽업 전용: 홈으로. 테이블은 미사용(#626 — 주문 현황 보기 제거). */
  onSecondary?: () => void;
  /** FCM 웹푸시 권한 안내 배너 라벨 (미지정 시 배너 미표시). */
  pushOptInLabels?: PushOptInBannerLabels;
  /** #694: 비회원 푸시 귀속 컨텍스트 — PushOptInBanner 게스트 분기로 전달 (메모리 전달만, 영속화 금지). */
  pushGuestContext?: GuestPushContext;
}

export function OrderSuccessScreen({
  orderType,
  tableNumber,
  justOrderedSummary,
  labels,
  formatCurrency,
  autoNavigateSeconds,
  onPrimary,
  onSecondary,
  pushOptInLabels,
  pushGuestContext,
}: OrderSuccessScreenProps) {
  // 라우팅 핸들러 중복 호출 방지 (같은 tick 더블탭 + 자동이동/수동탭 경합 차단).
  const hasNavigatedRef = useRef(false);

  const handlePrimary = useCallback(() => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    onPrimary();
  }, [onPrimary]);

  const handleSecondary = useCallback(() => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    onSecondary?.();
  }, [onSecondary]);

  // #626: 자동 이동 카운트다운 — 1초 간격 감소, 0 도달 시 primary 자동 실행.
  // 사용자가 먼저 탭하면 hasNavigatedRef 가드가 자동 실행을 무효화한다.
  const autoNavigate = (autoNavigateSeconds ?? 0) > 0;
  const [remainingSeconds, setRemainingSeconds] = useState(autoNavigateSeconds ?? 0);

  // codex R1 P2-1: prop 변경 시 remainingSeconds 재동기화 (render-time reset 패턴).
  // useState 초기값만 쓰면 undefined→N 전환(마운트 유지) 시 0 잔존 → 즉시 handlePrimary
  // 오발화. 렌더 중 reset 이라 effect 실행 전에 재렌더가 보장돼 stale 0 발화 경로가 없다.
  const [prevAutoNavigateSeconds, setPrevAutoNavigateSeconds] = useState(autoNavigateSeconds);
  if (prevAutoNavigateSeconds !== autoNavigateSeconds) {
    setPrevAutoNavigateSeconds(autoNavigateSeconds);
    setRemainingSeconds(autoNavigateSeconds ?? 0);
  }

  // #680 리뷰: 푸시 배너 노출 중에는 카운트다운 일시정지 — 배너를 읽고 응답할 시간 보장.
  // 배너가 사라지면(수락/나중에/언마운트) false 통지로 카운트다운 재개.
  // 배너 미노출(라벨 prop 없음/비로그인/미지원) 시에는 false 그대로 — 기존 동작 동일.
  const [pushBannerOpen, setPushBannerOpen] = useState(false);

  useEffect(() => {
    if (!autoNavigate || pushBannerOpen) return undefined;
    const intervalId = setInterval(() => {
      setRemainingSeconds((prev) => (prev > 0 ? prev - 1 : prev));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [autoNavigate, pushBannerOpen]);

  useEffect(() => {
    if (!autoNavigate || pushBannerOpen || remainingSeconds > 0) return;
    handlePrimary();
  }, [autoNavigate, pushBannerOpen, remainingSeconds, handlePrimary]);

  const isTable = orderType === 'table';
  const primaryLabel = isTable ? labels.viewMenu : labels.viewOrders;
  // #626: 테이블 모드 secondary("주문 현황 보기") 제거 — 픽업만 secondary(홈으로) 유지.
  const secondaryLabel = isTable ? null : labels.goHome;

  const summaryText =
    justOrderedSummary && formatCurrency
      ? `${labels.justOrderedPrefix} · ${justOrderedSummary.itemCount}${labels.itemCountUnit} · ${formatCurrency(
          justOrderedSummary.totalAmount,
          justOrderedSummary.currency,
        )}`
      : null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-success-title"
    >
      <div className={styles.content}>
        <svg
          className={styles.icon}
          viewBox="0 0 120 120"
          fill="none"
          aria-hidden="true"
        >
          <circle
            className={styles.circle}
            cx="60"
            cy="60"
            r="46"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
          />
          <path
            className={styles.check}
            d="M38 61.5L53.5 77L84 45"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <h2 id="order-success-title" className={styles.title}>
          {labels.title}
        </h2>
        <p className={styles.message}>{labels.thanks}</p>

        {summaryText && <p className={styles.summary}>{summaryText}</p>}
        {isTable && tableNumber && (
          <p className={styles.tableNumber}>{labels.tableLabel(tableNumber)}</p>
        )}

        {pushOptInLabels && (
          <PushOptInBanner
            labels={pushOptInLabels}
            guestContext={pushGuestContext}
            onInteractionChange={setPushBannerOpen}
          />
        )}

        <button
          type="button"
          className={styles.button}
          onClick={handlePrimary}
        >
          {primaryLabel}
          {/* #626: 자동 이동 잔여 초 표시 — 언어중립 "(N)". aria-hidden 으로
              접근성 이름은 라벨 그대로 유지(스크린리더에 매초 변동 노이즈 차단). */}
          {autoNavigate && remainingSeconds > 0 && (
            <span aria-hidden="true"> ({remainingSeconds})</span>
          )}
        </button>
        {secondaryLabel && onSecondary && (
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleSecondary}
          >
            {secondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
}
